//@flow
import * as React from 'react';
import {
    Position,
    Flex,
    Button,
    Columns,
    Box,
    Text,
    ToastProvider
} from '@locus-taxy/neo-ui';
import OutletsList from './OutletsList';
import TerritoryList from './TerritoryList';
import TerritoryMap from './TerritoryMap';
import ManualTerritoryProvider, {
    TerritoryContext
} from './ManualTerritoryProvider';
import {
    CreateTerritoryPlan,
    CreateTerritory
} from 'common/models/internal/CreateTerritoryPlan';
import { useTranslation } from 'react-i18next';
import { TerritoryAddEditContext } from 'common/entity/territoryMaster/territoryCreation/TerritoryAddEditProvider';
import { EnhancedInput } from 'common/components/core/input';
import * as Immutable from 'immutable';

type Props = {
    territoryPlan: CreateTerritoryPlan,
    isEditing: boolean,
    isManualPlan: boolean,
    clientId: string,
    addOutletsToPlan?: (Array<string>) => void,
    postingData: boolean,
    onManualPlanFreeze: () => void,
    onManualPlanSave: (territories: Immutable.List<CreateTerritory>) => void,
    onPlanNameChange: (name: string) => void
};

const ManualTerritoryCreation = (props): React.Node => {
    const { t } = useTranslation('entityManagement');
    const { onDismissTerritoryCreation } = React.useContext(
        TerritoryAddEditContext
    );

    const { territories, unallocatedOutlets } = React.useContext(
        TerritoryContext
    );
    const {
        onManualPlanSave,
        onPlanNameChange,
        territoryPlan,
        onManualPlanFreeze
    } = props;

    const [planNameError, setPlanNameError] = React.useState(null);

    const _onPlanNameChange = React.useCallback(
        value => {
            if (planNameError) {
                setPlanNameError(null);
            }
            onPlanNameChange(value);
        },
        [onPlanNameChange, planNameError]
    );

    const freezePlan = React.useCallback(() => {
        if (props.territoryPlan.planStatus !== 'FROZEN') {
            onManualPlanFreeze();
        }
    }, [onManualPlanFreeze, props.territoryPlan.planStatus]);

    const addManualTerritory = React.useCallback(() => {
        if (territoryPlan.name) {
            onManualPlanSave(territories);
        } else {
            setPlanNameError(t('createTerritory.planNameError'));
        }
    }, [onManualPlanSave, territories, territoryPlan.name]);

    const _onCancelCreateTerritory = React.useCallback(() => {
        onDismissTerritoryCreation(true);
    }, [onDismissTerritoryCreation]);

    const planFrozen = props.territoryPlan.planStatus === 'FROZEN';

    return (
        <Position
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            flexDirection="column"
            bg="white"
            display="flex"
            zIndex={15}
        >
            <Flex>
                <TerritoryList />
                <OutletsList />
                <Flex flex={8} flexDirection="column">
                    <Box p="2">
                        <Columns align="right" space="small">
                            <Box p={2}>
                                <Text fontSize={2} fontWeight={600}>
                                    {t('createTerritory.planName')}:{' '}
                                </Text>
                            </Box>
                            <EnhancedInput
                                onChange={_onPlanNameChange}
                                value={territoryPlan.name}
                                autoWidth
                                error={planNameError}
                                showRequired
                                type="text"
                            />
                            <Button
                                onClick={_onCancelCreateTerritory}
                                disabled={props.postingData}
                            >
                                {t('translations:cancel')}
                            </Button>
                            <Button
                                palette="primary"
                                appearance="solid"
                                onClick={addManualTerritory}
                                isLoading={props.postingData}
                            >
                                {t('translations:save')}
                            </Button>
                            <Button
                                palette="danger"
                                appearance="solid"
                                icon="lock"
                                onClick={freezePlan}
                                disabled={planFrozen || props.postingData}
                            >
                                {planFrozen
                                    ? t('createTerritory.frozen')
                                    : t('createTerritory.freeze')}
                            </Button>
                        </Columns>
                    </Box>
                    {(!!territories.size || !!unallocatedOutlets.size) && (
                        <TerritoryMap />
                    )}
                </Flex>
            </Flex>
        </Position>
    );
};

const ManualTerritoryCreationProvider = (props: Props): React.Node => {
    return (
        <ToastProvider>
            <ManualTerritoryProvider
                territories={props.territoryPlan.territories}
                planName={props.territoryPlan.name}
                planStatus={props.territoryPlan.planStatus}
                isEditing={props.isEditing}
                isManualPlan={props.isManualPlan}
                clientId={props.clientId}
            >
                <ManualTerritoryCreation {...props} />
            </ManualTerritoryProvider>
        </ToastProvider>
    );
};

export default ManualTerritoryCreationProvider;
